import { Button, ButtonLevel, type ButtonProps, ButtonSize } from '@vizit/atoms';
import type { IconProps } from '@vizit/icons';
import { Dialog, type DialogHeaderStatus, type DialogHeaderVariant, type DialogProps } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { type FieldValues, FormProvider, type UseFormReturn, useFieldArray, useFormContext } from 'react-hook-form';
import { Form } from '../form';

const FormDialogOpenContext = React.createContext<(() => void) | null>(null);

function FormDialogTrigger(props: React.PropsWithChildren) {
  const { children } = props;
  const onOpen = React.useContext(FormDialogOpenContext);

  return <span onClickCapture={onOpen ?? undefined}>{children}</span>;
}

function FormDialogTitle(props: React.PropsWithChildren) {
  const { children } = props;

  return <Dialog.Title>{children}</Dialog.Title>;
}

function FormDialogSubtitle(props: React.PropsWithChildren) {
  const { children } = props;

  return <Dialog.Subtitle>{children}</Dialog.Subtitle>;
}

function FormDialogFormFields(props: React.PropsWithChildren) {
  const { children } = props;

  return <>{children}</>;
}

interface FormDialogSubmitButtonProps extends Omit<ButtonProps, 'type' | 'isDisabled' | 'isLoading' | 'aria-label'> {}

function FormDialogSubmitButton(props: FormDialogSubmitButtonProps) {
  const { children, size = ButtonSize.Lg, level = ButtonLevel.Primary, className, ...rest } = props;

  const { formState } = useFormContext();

  return (
    <Button
      {...rest}
      className={cx('w-full', className)}
      size={size}
      level={level}
      type="submit"
      isDisabled={!formState.isValid}
      isLoading={formState.isSubmitting}
      aria-label={typeof children === 'string' ? children : 'Submit'}
    >
      {children}
    </Button>
  );
}

interface _FormDialogProps extends DialogProps {
  /**
   * The children of the dialog form
   */
  children: React.ReactNode;
  /**
   * A unique name for the form
   */
  formName: string;
  /**
   * The on reset handler of the form
   */
  onResetForm?: () => void;
  /**
   * The on submit handler of the form
   */
  onSubmit: React.FormEventHandler<HTMLFormElement> | ((event: React.FormEvent) => Promise<void>);
  /**
   * The on open change handler of the dialog
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * The class name of the form fields
   */
  formFieldsClassName?: string;
  /**
   * The variant of the dialog header
   * @type {DialogHeaderVariant}
   * @optional
   */
  headerVariant?: DialogHeaderVariant;
  /**
   * The status of the dialog header
   * @type {DialogHeaderStatus}
   * @optional
   */
  headerStatus?: DialogHeaderStatus;
  /**
   * Custom icon to display in the header
   * @type {React.ComponentType<IconProps>}
   * @optional
   */
  headerIcon?: React.ComponentType<IconProps>;
}

function _FormDialog(props: _FormDialogProps & { 'data-testid'?: string }) {
  const {
    children: childrenProp,
    isOpen: isOpenProp,
    formName,
    onResetForm,
    onSubmit,
    onOpenChange,
    formFieldsClassName = '',
    headerVariant,
    headerStatus,
    headerIcon,
    'data-testid': dataTestId,
    ...rest
  } = props;

  const { control, formState, unregister, register } = useFormContext();
  const { fields } = useFieldArray({
    control,
    name: formName,
  });

  const [isOpen, setIsOpen] = React.useState(isOpenProp || false);

  const handleResettingFormState = React.useCallback(
    (open: boolean) => {
      onResetForm?.();

      if (open) {
        // Re-register the fields on open.
        for (const { id } of fields) {
          register(id);
        }
      } else {
        // Unregister the fields to avoid errors or values being set.
        unregister(fields.map(({ id }) => id));
      }
    },
    [onResetForm, fields, register, unregister],
  );

  const handleOnOpenChange = React.useCallback(
    (open: boolean) => {
      handleResettingFormState(open);

      setIsOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange, handleResettingFormState],
  );

  const handleOpen = React.useCallback(() => {
    handleOnOpenChange(true);
  }, [handleOnOpenChange]);

  const children = React.Children.toArray(childrenProp);

  const triggerComponent = children.find((child) => React.isValidElement(child) && child.type === FormDialogTrigger);
  const formFieldsComponent = children.filter(
    (child) => React.isValidElement(child) && child.type === FormDialogFormFields,
  );
  const formTitleComponent = children.find((child) => React.isValidElement(child) && child.type === FormDialogTitle);
  const formSubtitleComponent = children.find(
    (child) => React.isValidElement(child) && child.type === FormDialogSubtitle,
  );
  const formSubmitButtonComponent = children.find(
    (child) => React.isValidElement(child) && child.type === FormDialogSubmitButton,
  );

  React.useEffect(() => {
    // if isOpen is controlled by parent component, update the state.
    if (isOpenProp !== undefined && isOpenProp !== isOpen && onOpenChange) {
      setIsOpen(isOpenProp);
    }
  }, [isOpenProp, isOpen, onOpenChange]);

  return (
    <FormDialogOpenContext.Provider value={handleOpen}>
      <Dialog isOpen={isOpen} onOpenChange={handleOnOpenChange} {...rest}>
        {triggerComponent}
        <Dialog.Overlay isDismissable={!formState.isSubmitting}>
          <Dialog.Content
            data-testid={dataTestId}
            closeButtonIsDisabled={formState.isSubmitting}
            aria-label={formName || 'Dialog'}
          >
            <Dialog.Header variant={headerVariant} status={headerStatus} icon={headerIcon}>
              {formTitleComponent}
              {formSubtitleComponent}
            </Dialog.Header>
            <Form onSubmit={onSubmit} className="flex flex-col flex-1 min-h-0 px-12">
              <div className={cx('overflow-y-auto flex-1', formFieldsClassName)}>{formFieldsComponent}</div>
              <Dialog.Footer className="mx-0">
                <Button
                  className="w-full"
                  size={ButtonSize.Lg}
                  level={ButtonLevel.Secondary}
                  onPress={() => handleOnOpenChange(false)}
                  isDisabled={formState.isSubmitting}
                  aria-label="Cancel and close dialog"
                >
                  Cancel
                </Button>
                {formSubmitButtonComponent}
              </Dialog.Footer>
            </Form>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog>
    </FormDialogOpenContext.Provider>
  );
}

interface FormDialogProps<T extends FieldValues> extends _FormDialogProps {
  /**
   * The methods from the `react-hook-form` useForm hook.
   *
   * @example
   * ```tsx
   * const formMethods = useForm<FormSchema>();
   * ```
   */
  formMethods: UseFormReturn<T>;
}

/**
 * A dialog component that wraps a form and provides a trigger, title, subtitle, form fields, and a submit button.
 * This component uses `react-hook-form` to manage the form state and validation.
 *
 * @param props - The props for the form dialog.
 * @returns A dialog component that wraps a form and provides a trigger, title, subtitle, form fields, and a submit button.
 *
 * @example
 * ```tsx
 * const formMethods = useForm<FormSchema>();
 *
 * return (
 * <FormDialog
 *   formMethods={formMethods}
 *   formName="test-dialog"
 *   onSubmit={handleSubmit(onSubmit)}
 *   onResetForm={handleResetForm}
 * >
 *  <FormDialog.Trigger>
 *    <Button>Open Dialog</Button>
 *  </FormDialog.Trigger>
 *  <FormDialog.Title>Test Dialog</FormDialog.Title>
 *  <FormDialog.Subtitle>This is a test dialog</FormDialog.Subtitle>
 *  <FormDialog.FormFields>
 *    <FormTextField name="name" label="Name" isRequired />
 *    <FormTextField name="email" label="Email" isRequired />
 *  </FormDialog.FormFields>
 *  <FormDialog.SubmitButton>Submit</FormDialog.SubmitButton>
 * </FormDialog>
 * );
 * ```
 */
function FormDialog<T extends FieldValues>(props: FormDialogProps<T>) {
  const { children, formMethods, ...rest } = props;

  return (
    <FormProvider {...formMethods}>
      <_FormDialog {...rest}>{children}</_FormDialog>
    </FormProvider>
  );
}

export {
  FormDialog,
  FormDialogTrigger,
  FormDialogTitle,
  FormDialogSubtitle,
  FormDialogFormFields,
  FormDialogSubmitButton,
};
export type { FormDialogProps };
